import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { lang } from "src/language/Strings";
import { useEffect } from "react";
import { useMemo } from "react";
import { useRef } from "react";
import { useState } from "react";
import { useApplyFilters } from "src/utils/applyFilter";
import { objectTypes } from "src/consts/objectTypes";
import SearchInput from "./SearchInput";
import FilterButton from "./FilterButton";
import { InspectionDataCard } from "src/components/InspectionDataCard";
import { MutableRefObject } from "react";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { routes } from "src/routes";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";
import Toggle from "./Toggle";
import { InspectionStatus } from "src/types/InspectionStatus";

export function ListInspections({ objectType }: { objectType: string }) {
  const { inspectionId, companyId } = useParams();
  const { data: inspections } = useProjectVariations();
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollListRef = useRef<HTMLDivElement | null>(null);
  const [showCompleted, setShowCompleted] = useState(true);
  const { data: inspection } = useProjectVariationById();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const applyFilters = useApplyFilters();
  useChangeTitle(
    objectType === objectTypes.Inspection
      ? lang.Egenkontroller
      : objectType === objectTypes.Deviation && !inspectionId
      ? lang.deviations
      : lang.deviationsForInspectionAddress(
          inspection?.title || inspection?.title || ""
        )
  );
  useEffect(() => {
    if (
      location.pathname.includes(
        routes.Egenkontroller.Pages.Deviations.List.url(companyId, inspectionId)
      )
    ) {
      const element = document.getElementById("plus-icon-add");
      const listener = () => {
        navigate(
          routes.Egenkontroller.Pages.Deviations.Create.url(
            companyId,
            inspectionId
          )
        );
      };
      element?.addEventListener("click", listener);
      return () => {
        element?.removeEventListener("click", listener);
      };
    }
  }, []);

  useEffect(
    () =>
      observeListChanges(
        setVisibleCards,
        scrollListRef,
        inspections,
        observerRef
      ),
    [inspections]
  );

  const filteredDeviations = useMemo(() => {
    const deviationsFiltered = applyFilters(inspections || []).filter((i) => {
      // Check if item should be shown based on completion status
      const showCompletedItem = showCompleted
        ? true
        : Number(i.status) !== Number(InspectionStatus.Finished);

      // Check if item matches search text
      const searchMatch =
        searchText === "" ||
        Object.keys(i).some((key) => {
          return String(i[key as keyof InspectionResponse])
            .toLowerCase()
            .includes(searchText.toLowerCase());
        });

      return showCompletedItem && searchMatch;
    });
    switch (objectType) {
      case "inspection":
      case objectTypes.Deviation:
        if (inspectionId) {
          return deviationsFiltered?.filter((i) => {
            return (
              i.parentDeviationId === Number(inspectionId) &&
              i.objectType === objectTypes.Deviation
            );
          });
        }
        if (!inspectionId) {
          return deviationsFiltered?.filter((i) => {
            return i.objectType === objectTypes.Deviation;
          });
        }
      case "Egenkontroller":
      case objectTypes.Inspection:
        return deviationsFiltered?.filter((i) => {
          return i.objectType === objectTypes.Inspection;
        });
      default:
        return deviationsFiltered;
    }
  }, [inspections, inspectionId, objectType, searchText, showCompleted]);

  return (
    <div className="flex flex-col h-full relative w-full ">
      <div className="flex flex-row w-full p-2">
        <div className="flex-1 min-w-0">
          <SearchInput onChange={setSearchText} />
        </div>
        <FilterButton />
      </div>
      <div className="flex-shrink-0 flex flex-row gap-2 pl-4">
        <Toggle
          name="showCompleted"
          falseLabel={lang.showCompleted}
          trueLabel={lang.showCompleted}
          falseValue={false}
          trueValue={true}
          defaultValue={showCompleted}
          onChange={(e) => {
            setShowCompleted(e.target.checked);
          }}
        />
      </div>
      <div className="flex flex-col gap-1 overflow-y-auto border-t-2 border-Grey4 mt-4 pb-2">
        {filteredDeviations
          ?.sort((a, b) => b.id - a.id)
          .map((pV) => (
            <InspectionDataCard
              objectType={objectType}
              key={pV.id}
              item={pV as InspectionResponse}
              visibleComponents={visibleCards}
            />
          ))}
        {!filteredDeviations ||
          (filteredDeviations?.length === 0 && (
            <small className="text-PrimaryRed text-center w-full h-full">
              {objectType === objectTypes.Inspection
                ? lang.noInspectionsAdded
                : lang.noDeviationsAdded}
            </small>
          ))}
      </div>
    </div>
  );
}

function observeListChanges(
  setVisibleCards: React.Dispatch<React.SetStateAction<number[]>>,
  scrollListRef: React.MutableRefObject<HTMLDivElement | null>,
  projectVariations: InspectionResponse[] | undefined,
  observerRef: MutableRefObject<IntersectionObserver | null>
) {
  if (!scrollListRef?.current || !projectVariations) return;

  if (observerRef?.current) {
    observerRef?.current.disconnect();
  }

  observerRef.current = new IntersectionObserver(
    (entries) => {
      setVisibleCards((prevVisibleCards) => {
        const updatedVisibleCards = new Set(prevVisibleCards);

        entries.forEach((entry) => {
          const cardId = entry.target.getAttribute("id");
          if (!cardId) return;

          const cardNumber = Number(
            cardId.replace("projectvariationDatacard-", "")
          );
          if (entry.isIntersecting) {
            updatedVisibleCards.add(cardNumber);
          } else {
            updatedVisibleCards.delete(cardNumber);
          }
        });

        return Array.from(updatedVisibleCards);
      });
    },
    {
      root: scrollListRef.current,
      threshold: 0.5,
    }
  );

  projectVariations.forEach((pV) => {
    const element = document.getElementById(
      `projectvariationDatacard-${pV?.id}`
    );
    if (element && observerRef.current) {
      observerRef.current.observe(element);
    }
  });

  return () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  };
}
